import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  CreditCard,
  Savings,
  Warning,
  AttachMoney,
  MonetizationOn,
  Home as HomeIcon,
  AccountBalanceWallet
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import debtService from '../../../services/debtService';
import savingsService from '../../../services/savingsService';
import expenseService from '../../../services/expenseService';
import walletService from '../../wallet/services/walletService';
import AssetLiabilityOverview from './AssetLiabilityOverview';

// Helper function to format large numbers with SI prefixes and determine text size
const formatLargeValue = (value) => {
  // Ensure value is a number
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  const absValue = Math.abs(numValue);
  const isNegative = numValue < 0;
  
  if (absValue >= 10000000) {
    // 10M+ - Use SI prefixing
    const millions = absValue / 1000000;
    const formatted = millions.toFixed(1);
    return {
      display: `${isNegative ? '-' : ''}$${formatted}M`,
      sizeClass: 'text-[7px] sm:text-[9px] md:text-[10px] lg:text-xs xl:text-sm',
      fullValue: formatCurrency(numValue)
    };
  } else if (absValue >= 1000000) {
    // 1M+ - Use SI prefixing
    const millions = absValue / 1000000;
    const formatted = millions.toFixed(2);
    return {
      display: `${isNegative ? '-' : ''}$${formatted}M`,
      sizeClass: 'text-[7px] sm:text-[9px] md:text-[10px] lg:text-xs xl:text-sm',
      fullValue: formatCurrency(numValue)
    };
  } else if (absValue >= 100000) {
    // 100K+ - Use K prefixing
    const thousands = absValue / 1000;
    const formatted = thousands.toFixed(0);
    return {
      display: `${isNegative ? '-' : ''}$${formatted}K`,
      sizeClass: 'text-[8px] sm:text-[10px] md:text-xs lg:text-sm xl:text-base',
      fullValue: formatCurrency(numValue)
    };
  } else {
    // Under 100K - Full format
    return {
      display: formatCurrency(numValue),
      sizeClass: 'text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg',
      fullValue: formatCurrency(numValue)
    };
  }
};

const SummarySection = ({ expenses, debts, savings, assets = [], summary, isPartnerMode = false, isOurMode = false }) => {
  const [loading, setLoading] = useState(false);
  const [debtStats, setDebtStats] = useState({ 
    totalDebt: 0,
    monthlyPayment: 0
  });
  const [savingsStats, setSavingsStats] = useState({ 
    totalSavings: 0 
  });
  const [expenseStats, setExpenseStats] = useState({
    totalMonthly: 0
  });
  const [netWorthData, setNetWorthData] = useState(null);
  const [checkingTotal, setCheckingTotal] = useState(0);

  useEffect(() => {
    const fetchAllStats = async () => {
      setLoading(true);
      
      // Clear old data when switching modes
      setDebtStats({ totalDebt: 0, monthlyPayment: 0 });
      setSavingsStats({ totalSavings: 0 });
      setExpenseStats({ totalMonthly: 0 });
      setNetWorthData(null);
      setCheckingTotal(0);
      
      // Clear service caches to force fresh data fetch
      debtService.clearCache();
      savingsService.clearCache();
      expenseService.clearCache();
      
      try {
        console.log('🔍 SummarySection fetchAllStats - isPartnerMode:', isPartnerMode, 'isOurMode:', isOurMode, 'summary exists:', !!summary);
        
        // Pass both modes to service calls
        const [debt, savings, expense, netWorth] = await Promise.all([
          debtService.getDebtStatistics(isPartnerMode, isOurMode),
          savingsService.getSavingsStatistics(isPartnerMode, isOurMode),
          expenseService.getExpenseStatistics(isPartnerMode, isOurMode),
          walletService.getNetWorth(isPartnerMode, isOurMode)
        ]);
        
        console.log("Fetched Debt Stats:", debt);
        console.log("Fetched Savings Stats:", savings);
        console.log("Fetched Expense Stats:", expense);
        console.log("Fetched Net Worth Data:", netWorth);
        
        setDebtStats(debt);
        setSavingsStats(savings);
        setExpenseStats(expense);
        
        if (netWorth.success) {
          setNetWorthData(netWorth.data);
          
          // Calculate total checking accounts from assets (where Plaid depository accounts are stored)
          const assets = netWorth.data.breakdown?.assets || [];
          
          // Helper function to get account balance with multiple fallbacks (same as AssetLiabilityOverview)
          const getAccountBalance = (account) => {
            // For assets (checking/savings), try these fields
            if (account.type === 'depository') {
              return account.currentBalance || account.availableBalance || account.balance || 0;
            }
            // Generic fallback
            return account.currentBalance || account.availableBalance || account.balance || 0;
          };
          
          const checking = assets
            .filter(acc => {
              // Only include ACTUAL checking accounts, not all depository accounts
              const isChecking = (acc.subtype === 'checking') ||
                                (acc.name && acc.name.toLowerCase().includes('checking'));
              console.log('🏦 Checking account filter:', {
                name: acc.name,
                accountType: acc.accountType,
                subtype: acc.subtype,
                type: acc.type,
                isChecking,
                reason: acc.subtype === 'checking' ? 'subtype=checking' : 
                       (acc.name && acc.name.toLowerCase().includes('checking')) ? 'name contains checking' : 'not checking',
                rawBalance: acc.currentBalance || acc.balance
              });
              return isChecking;
            })
            .reduce((sum, acc) => {
              // Use the proper balance calculation
              const balance = getAccountBalance(acc);
              console.log(`💰 Adding to checking total: ${acc.name} = $${balance} (type: ${acc.type}, subtype: ${acc.subtype})`);
              return sum + balance;
            }, 0);
          
          console.log(`🏦 Total Checking Calculated: $${checking}`);
          setCheckingTotal(checking);
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllStats();
  }, [isPartnerMode, isOurMode]);

  if (!summary) return null;

  // Define these values with default fallbacks from our stat services
  const totalMonthlyExpenses = expenseStats.totalMonthly || 0;
  const totalDebt = debtStats.totalDebt || 0;
  const totalMonthlyDebtPayments = debtStats.monthlyPayment || 0;
  const totalSavings = savingsStats.totalSavings || 0;
  const totalSavingsTarget = savingsStats.totalSavings * 1.5 || 0; // Estimate if not available
  const savingsProgress = savingsStats.goalProgress || 0;
  const upcomingPayments = expenseStats.upcomingPayments || [];
  const monthlyGrossIncome = summary.monthlyGrossIncome || 0;
  const monthlyNetIncome = summary.monthlyNetIncome || 0;

  // Use proper net worth calculation from API, fallback to simplified calculation
  const properNetWorth = netWorthData?.netWorth || 0;
  const properTotalAssets = netWorthData?.totalAssets || 0;
  const properTotalLiabilities = netWorthData?.totalLiabilities || 0;
  
  // Debug logging for liabilities
  console.log('💳 Total Liabilities Debug:', {
    fromAPI: properTotalLiabilities,
    fromDebtService: totalDebt,
    liabilitiesBreakdown: netWorthData?.breakdown?.liabilities,
    willUse: properTotalLiabilities || totalDebt
  });
  
  // Use API value as primary source, but add client-side validation for credit cards
  let finalLiabilities = properTotalLiabilities;
  
  // If we have breakdown data, verify the calculation makes sense
  if (netWorthData?.breakdown?.liabilities?.length > 0) {
    const clientCalculated = netWorthData.breakdown.liabilities.reduce((sum, liability) => {
      // For credit cards, only use currentBalance (debt), not availableBalance (credit limit)
      let balance = 0;
      if (liability.type === 'credit') {
        balance = liability.currentBalance || 0;
        console.log(`💳 Client-side credit calc: ${liability.name} currentBalance=${liability.currentBalance}, availableBalance=${liability.availableBalance}, using=${balance}`);
      } else {
        balance = liability.currentBalance || liability.balance || 0;
      }
      return sum + Math.abs(balance);
    }, 0);
    
    console.log('🔧 API vs Client liability calculation:', {
      api: properTotalLiabilities,
      clientCalculated,
      difference: Math.abs(properTotalLiabilities - clientCalculated)
    });
    
    // If there's a big difference (>$1000), likely the API is using wrong balance field
    if (Math.abs(properTotalLiabilities - clientCalculated) > 1000) {
      console.log('🔧 Large discrepancy detected - using client calculation');
      finalLiabilities = clientCalculated;
    } else {
      console.log('🔧 Using API liabilities value (small discrepancy)');
      finalLiabilities = properTotalLiabilities;
    }
  } else {
    console.log('🔧 Using API liabilities value (no breakdown data):', finalLiabilities);
  }
  
  // Calculate asset totals from assets collection for backup
  const assetsCollectionValue = Array.isArray(assets) ? assets.reduce((total, asset) => total + (asset.currentValue || 0), 0) : 0;
  
  // Use proper values if available, otherwise fallback
  const netWorth = properNetWorth || (assetsCollectionValue - totalDebt);
  const totalAssetValue = properTotalAssets || assetsCollectionValue;
  const totalLiabilities = finalLiabilities || totalDebt;

  // Calculate disposable income (net income minus expenses, debt payments, and planned monthly savings)
  const monthlyPlannedSavings = savingsStats.monthlySavingsRate || 0;
  const disposableIncome = monthlyNetIncome - totalMonthlyExpenses - totalMonthlyDebtPayments - monthlyPlannedSavings;

  // Calculate income utilization percentages
  const expensesPercentage = monthlyGrossIncome > 0 
    ? (totalMonthlyExpenses / monthlyGrossIncome) * 100 
    : 0;
  const debtPercentage = monthlyGrossIncome > 0 
    ? (totalMonthlyDebtPayments / monthlyGrossIncome) * 100 
    : 0;
  const savingsPercentage = monthlyGrossIncome > 0 
    ? (monthlyPlannedSavings / monthlyGrossIncome) * 100 
    : 0;
  const disposablePercentage = monthlyGrossIncome > 0 
    ? (disposableIncome / monthlyGrossIncome) * 100 
    : 0;

  // Financial overview cards with proper keys
  const financialCards = [
    {
      id: "gross-income",
      title: "Monthly Gross Income",
      value: monthlyGrossIncome,
      description: "Before taxes and deductions",
      icon: <MonetizationOn className="h-5 w-5 text-indigo-600" />,
      bgColor: "from-indigo-50 to-indigo-100/90",
      textColor: "text-indigo-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "net-income",
      title: "Monthly Net Income",
      value: monthlyNetIncome,
      description: "After taxes and deductions",
      icon: <AttachMoney className="h-5 w-5 text-green-600" />,
      bgColor: "from-green-50 to-green-100/90",
      textColor: "text-green-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "net-worth",
      title: "Net Worth",
      value: netWorth,
      description: `Assets (${formatCurrency(totalAssetValue)}) - Liabilities (${formatCurrency(totalLiabilities)})`,
      icon: <AccountBalanceWallet className="h-5 w-5 text-emerald-600" />,
      bgColor: "from-emerald-50 to-emerald-100/90",
      textColor: netWorth >= 0 ? "text-emerald-600" : "text-red-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "total-assets",
      title: "Total Asset Value",
      value: totalAssetValue,
      description: `${assets.length} assets tracked`,
      icon: <HomeIcon className="h-5 w-5 text-teal-600" />,
      bgColor: "from-teal-50 to-teal-100/90",
      textColor: "text-teal-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "monthly-expenses",
      title: "Monthly Expenses",
      value: totalMonthlyExpenses,
      description: `${expensesPercentage.toFixed(1)}% of gross income`,
      icon: <TrendingDown className="h-5 w-5 text-red-600" />,
      bgColor: "from-red-50 to-red-100/90",
      textColor: "text-red-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "total-liabilities",
      title: "Total Liabilities",
      value: totalLiabilities,
      description: `${((totalLiabilities / monthlyGrossIncome) * 100).toFixed(1)}% of gross income`,
      icon: <AccountBalance className="h-5 w-5 text-orange-600" />,
      bgColor: "from-orange-50 to-orange-100/90",
      textColor: "text-orange-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "current-checkings",
      title: "Current Checkings",
      value: checkingTotal,
      description: `${((checkingTotal / monthlyGrossIncome) * 100).toFixed(1)}% of monthly income`,
      icon: <CreditCard className="h-5 w-5 text-blue-600" />,
      bgColor: "from-blue-50 to-blue-100/90",
      textColor: "text-blue-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "total-savings",
      title: "Total Savings",
      value: totalSavings,
      description: `${savingsPercentage.toFixed(1)}% of gross income`,
      icon: <Savings className="h-5 w-5 text-emerald-600" />,
      bgColor: "from-emerald-50 to-emerald-100/90",
      textColor: "text-emerald-600",
      colSpan: "lg:col-span-2"
    },
    {
      id: "disposable-income",
      title: "Disposable Income",
      value: disposableIncome,
      description: `${disposablePercentage.toFixed(1)}% of gross income`,
      icon: <TrendingUp className="h-5 w-5 text-purple-600" />,
      bgColor: "from-purple-50 to-purple-100/90",
      textColor: "text-purple-600",
      colSpan: "lg:col-span-2"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Financial Overview */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent mb-3 sm:mb-4">Financial Overview</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          {financialCards.map(card => {
            // Format value with SI prefixes for large numbers
            const valueInfo = formatLargeValue(card.value);
            // Debug logging
            if (Math.abs(card.value) >= 100000) {
              console.log(`💰 ${card.title}: ${card.value} -> ${valueInfo.display}`);
            }
            
            return (
              <div 
                key={card.id} 
                className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] sm:shadow-[0_15px_45px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_20px_55px_-12px_rgba(0,0,0,0.6)] rounded-xl p-2 sm:p-3 md:p-4 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] flex flex-col items-center"
              >
                {/* Icon and Title - Centered */}
                <div className="flex flex-col items-center mb-1.5 sm:mb-2 w-full">
                  <div className={`bg-gradient-to-br ${card.bgColor} p-1 sm:p-1.5 md:p-2 rounded-lg sm:rounded-xl shadow-md flex-shrink-0 mb-2 sm:mb-2.5 flex items-center justify-center`}>
                    <div className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex items-center justify-center">
                      {React.cloneElement(card.icon, { className: "h-full w-full" })}
                    </div>
                  </div>
                  <Typography variant="subtitle2" className="text-[9px] sm:text-xs md:text-sm text-gray-600 font-medium leading-tight text-center px-1 line-clamp-2">
                    {card.title}
                  </Typography>
                </div>
                {/* Value - Centered, with SI prefixes for large numbers */}
                <Typography 
                  variant="h5" 
                  className={`${valueInfo.sizeClass} font-bold ${card.textColor} drop-shadow-sm mb-0.5 sm:mb-1 text-center whitespace-nowrap px-1`}
                  title={valueInfo.fullValue}
                >
                  {valueInfo.display}
                </Typography>
                {/* Description - Centered */}
                <Typography variant="caption" className="text-[8px] sm:text-[9px] md:text-xs text-gray-500 leading-tight text-center px-1">
                  {card.description}
                </Typography>
              </div>
            );
          })}
        </div>
      </div>

      {/* Asset & Liability Overview */}
      <AssetLiabilityOverview netWorthData={netWorthData} isPartnerMode={isPartnerMode} />

      {/* Savings Progress - Only show if there's meaningful savings data */}
      {(totalSavings > 0 || totalSavingsTarget > 0) && (
        <div className="bg-white/95 backdrop-blur-md rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Savings Progress</h2>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Progress</span>
              <span>{formatCurrency(totalSavings)} of {formatCurrency(totalSavingsTarget)}</span>
            </div>
            <div className="relative pt-1">
              <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                <div
                  style={{ width: `${savingsProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500 transition-all duration-500"
                />
              </div>
            </div>
            <div className="text-sm text-gray-500 text-right">
              {savingsProgress.toFixed(1)}% of target reached
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Payments */}
      <div className="bg-white/95 backdrop-blur-md rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Upcoming Payments</h2>
        <div className="divide-y divide-gray-200">
          {upcomingPayments && upcomingPayments.length > 0 ? (
            upcomingPayments.map((payment, index) => (
              <div key={payment._id || payment.id || `payment-${index}`} className="py-3 flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`p-2 rounded-full mr-3 ${
                    payment.type === 'expense' 
                      ? 'bg-red-100 text-red-600'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {payment.type === 'expense' ? <TrendingDown className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{payment.name}</p>
                    <p className="text-sm text-gray-500">Due: {formatDate(payment.dueDate)}</p>
                  </div>
                </div>
                <Typography variant="h6" className={payment.type === 'expense' ? 'text-red-600' : 'text-blue-600'}>
                  {formatCurrency(payment.amount)}
                </Typography>
              </div>
            ))
          ) : (
            <div className="py-3 text-center text-gray-500">
              No upcoming payments
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummarySection; 